import { create } from 'zustand';
import { webTracking } from '../utils/webTracking';

export type TrackingTarget = { osId: number; faseId: number };

const NATIVE_TARGETS_KEY = 'motorista_native_targets';

const mesmoAlvo = (a: TrackingTarget, b: TrackingTarget) =>
  a.osId === b.osId && a.faseId === b.faseId;

const lerNativeTargets = (): TrackingTarget[] => {
  try {
    const salvos = JSON.parse(localStorage.getItem(NATIVE_TARGETS_KEY) || '[]');
    return Array.isArray(salvos) ? salvos : [];
  } catch {
    return [];
  }
};

const salvarNativeTargets = (targets: TrackingTarget[]) => {
  if (targets.length === 0) {
    localStorage.removeItem(NATIVE_TARGETS_KEY);
  } else {
    localStorage.setItem(NATIVE_TARGETS_KEY, JSON.stringify(targets));
  }
};

interface TrackingState {
  /** Targets rastreados pelo navegador (mesma posição enviada para todos). */
  activeTargets: TrackingTarget[];
  /** Targets cujo rastreamento foi entregue ao app nativo. */
  nativeTargets: TrackingTarget[];
  /** Mantido por compatibilidade com UI atual (primeiro target). */
  osId: number | null;
  /** Mantido por compatibilidade com UI atual (primeiro target). */
  faseId: number | null;
  isTracking: boolean;
  trackingError: string | null;
  lastLocation: {
    lat: number;
    lng: number;
    timestamp: string;
  } | null;
  startTracking: (osId: number, faseId: number) => void;
  stopTracking: (osId?: number, faseId?: number) => void;
  delegarAoAppNativo: (osId: number, faseId: number) => void;
  removerDelegacaoNativa: (osId: number, faseId: number) => void;
  /** Alinha o rastreamento às fases do motorista que estão "Em Rota" (ex.: após recarregar a página). */
  sincronizarComFasesEmRota: (emRota: TrackingTarget[]) => void;
  updateLocation: (lat: number, lng: number) => void;
}

const derivar = (activeTargets: TrackingTarget[]) => {
  webTracking.atualizarAlvos(activeTargets);
  const primary = activeTargets[0] ?? null;
  return {
    activeTargets,
    isTracking: activeTargets.length > 0,
    osId: primary ? primary.osId : null,
    faseId: primary ? primary.faseId : null,
  };
};

export const useTrackingStore = create<TrackingState>((set, get) => {
  webTracking.configurar({
    onLocation: (lat, lng) => get().updateLocation(lat, lng),
    onError: (message) => set({ trackingError: message }),
  });

  return {
    activeTargets: [],
    nativeTargets: lerNativeTargets(),
    osId: null,
    faseId: null,
    isTracking: false,
    trackingError: null,
    lastLocation: null,

    startTracking: (osId: number, faseId: number) => {
      const alvo = { osId, faseId };
      set((state) => {
        if (state.nativeTargets.some((t) => mesmoAlvo(t, alvo))) return {};
        if (state.activeTargets.some((t) => mesmoAlvo(t, alvo))) return {};
        return derivar([...state.activeTargets, alvo]);
      });
    },

    stopTracking: (osId?: number, faseId?: number) => {
      set((state) => {
        const removingSpecific = typeof osId === 'number' && typeof faseId === 'number';
        const activeTargets = removingSpecific
          ? state.activeTargets.filter((t) => !(t.osId === osId && t.faseId === faseId))
          : [];
        return derivar(activeTargets);
      });
    },

    delegarAoAppNativo: (osId: number, faseId: number) => {
      const alvo = { osId, faseId };
      set((state) => {
        const nativeTargets = state.nativeTargets.some((t) => mesmoAlvo(t, alvo))
          ? state.nativeTargets
          : [...state.nativeTargets, alvo];
        salvarNativeTargets(nativeTargets);
        return {
          nativeTargets,
          ...derivar(state.activeTargets.filter((t) => !mesmoAlvo(t, alvo))),
        };
      });
    },

    removerDelegacaoNativa: (osId: number, faseId: number) => {
      const alvo = { osId, faseId };
      set((state) => {
        const nativeTargets = state.nativeTargets.filter((t) => !mesmoAlvo(t, alvo));
        salvarNativeTargets(nativeTargets);
        return { nativeTargets };
      });
    },

    sincronizarComFasesEmRota: (emRota: TrackingTarget[]) => {
      set((state) => {
        const nativeTargets = state.nativeTargets.filter((t) =>
          emRota.some((e) => mesmoAlvo(e, t))
        );
        const activeTargets = emRota.filter(
          (e) => !nativeTargets.some((t) => mesmoAlvo(t, e))
        );
        const mudouAtivos =
          activeTargets.length !== state.activeTargets.length ||
          activeTargets.some((a) => !state.activeTargets.some((t) => mesmoAlvo(t, a)));
        if (nativeTargets.length !== state.nativeTargets.length) {
          salvarNativeTargets(nativeTargets);
        }
        return {
          nativeTargets,
          ...(mudouAtivos ? derivar(activeTargets) : {}),
        };
      });
    },

    updateLocation: (lat: number, lng: number) => {
      set({
        lastLocation: {
          lat,
          lng,
          timestamp: new Date().toISOString(),
        },
      });
    },
  };
});
